import React, { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import { Provider } from 'react-redux'

import authorization from 'live/services/authorization'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { overviewDisplayAPI, OverviewProps } from 'plugin-codaf-risk-ext-bowtie/shared/api'
import { IAlarmEvent, IAlarmSettings } from 'plugin-codaf-risk-ext-bowtie/types/alarms'

import {
    getBowtie,
    listAlarp,
    listAllBowtiesWithDetails,
    getAlarmSettings,
    getResourceGroups,
    getBarrierStatus
} from '../services'
import { clearAllStore, store } from '../store'
import {
    IBowtie,
    IThreat,
    IConsequence,
    ITopEvent,
    ChartMessage,
    ChartState,
    Configs,
    IEscalationFactor,
    IBarrier,
    IAlarpThreshold,
    IAlarp,
    ResourceGroup,
    ChartEvent,
    IBarrierStatusSpecialStatus
} from '../types'
import { BOWTIE_SETTINGS_URL } from '../utils/constants'
import LoadingImage from '../utils/svg/loading.gif'
import { BowtieStatusContextProvider } from './BowtieStatusContext'
import { BowtieView, EscalationFactorView, BarrierView, ConsequenceView, ThreatView, TopEventView } from './BowtieViews'
import { AssetSelector, Bowtie } from './components'
import { AlarmsDashboard } from './components/AlarmsDashboard/AlarmsDashboard'
import { IOverviewAlarmEvent, IOverviewAsset } from './components/AssetSelector/AssetSelector'
import { AbsoluteLoadingContainer, CustomOverviewAlert, OverviewContainer } from './styles'

type LiveWidget = LiveReactChartProps<ChartState, ChartMessage, Configs>

export const BowtieManagement: React.FC<LiveWidget> = ({ width, height, state, widget }) => {
    const [inLoading, setInLoading] = useState(true)
    const [alarpList, setAlarpList] = useState<IAlarp[]>([])
    const [bowtieViews, setBowtieViews] = useState<BowtieView[]>([])
    const [selectedBowtie, setSelectedBowtie] = useState<number>()

    const [showAlarmsDashboard, setShowAlarmsDashboard] = useState<boolean>(false)
    const [alarms, setAlarms] = useState<Map<number, IAlarmEvent>>(state.alarms)
    const [assetToAlarm, setAssetToAlarm] = useState<IOverviewAsset>()
    const [alarmData, setAlarmData] = useState<IOverviewAlarmEvent>()
    const [alarmBowties, setAlarmBowties] = useState<IBowtie[]>()
    const [userInfo, setUserInfo] = useState<IAlarmSettings>()
    const [barrierStatusList, setBarrierStatusList] = useState<IBarrierStatusSpecialStatus[]>()
    const [statusMap, setStatusMap] = useState<Map<number, IBarrierStatusSpecialStatus>>(new Map())

    const currentUser: string = authorization.user.username

    const verifyBowtieSelected = (idBowtie: number, selectedsBowties: number[]): boolean => {
        if (selectedsBowties?.includes(idBowtie)) return true
    }

    const dispatchAlarmSound = async (alarm: ChartEvent): Promise<void> => {
        const verifier = userInfo?.selectedBowties.map(selectedBowtie => {
            return verifyBowtieSelected(selectedBowtie.bowtieId, alarm?.bowties_id)
        })

        if (verifier?.includes(true) && userInfo?.soundNotification) {
            new Audio('/content/plugin-codaf-risk-ext-bowtie/WarningAlarm.mp3').play()
        }
    }

    function getEscalationFactorView(escalationFactor: IEscalationFactor): EscalationFactorView {
        // eslint-disable-next-line  @typescript-eslint/no-use-before-define
        return new EscalationFactorView(escalationFactor, escalationFactor.barriers.map(getBarrierView))
    }

    function getBarrierView(barrier: IBarrier): BarrierView {
        return new BarrierView(barrier, barrier.escalations.map(getEscalationFactorView))
    }

    function getConsequenceView(consequence: IConsequence): ConsequenceView {
        return new ConsequenceView(consequence, consequence.barriers.map(getBarrierView))
    }

    function getThreatView(threat: IThreat): ThreatView {
        return new ThreatView(threat, threat.barriers.map(getBarrierView))
    }

    function getTopEventView(topEvent: ITopEvent): TopEventView {
        return new TopEventView(
            topEvent,
            topEvent.threats.map(getThreatView),
            topEvent.consequences.map(getConsequenceView)
        )
    }

    function getBowtieView(bowtie: IBowtie): BowtieView {
        return new BowtieView(bowtie, getTopEventView(bowtie.topEvent))
    }

    const Overview = React.useCallback(
        (props: OverviewProps): JSX.Element => {
            try {
                const { element: Element, uid } = overviewDisplayAPI.getDefaultOverview()

                if (Element && React.isValidElement(<Element {...props} />)) {
                    return (
                        <>
                            <Element {...props} height={(props?.height ?? height) - 32} />
                            {uid !== 'default' && selectedBowtie == null && (
                                <CustomOverviewAlert>
                                    <i className="far fa-exclamation-circle"></i>
                                    {i18n(
                                        'This is a custom client overview. It does not follow the original product standard.'
                                    )}
                                </CustomOverviewAlert>
                            )}
                        </>
                    )
                }

                throw new Error(`Unable to load selected overview ${uid}`)
            } catch (e) {
                return <AssetSelector {...props} />
            }
        },
        [selectedBowtie]
    )

    function isValidBowtie(bowtie: IBowtie): boolean {
        const hasConsequenceBarriers = bowtie.topEvent.consequences.every(cons => cons.barriers?.length)
        const hasThreatBarriers = bowtie.topEvent.threats.every(tr => tr.barriers?.length)

        return hasThreatBarriers && hasConsequenceBarriers
    }

    const getBowtiesFromAlarm = async (): Promise<void> => {
        try {
            const requests = alarmData?.bowties_id?.map(id => {
                return getBowtie(id)
            })
            await Promise.all(requests).then(values => {
                setAlarmBowties(values)
            })
        } catch (error) {}
    }

    async function getAlarp(): Promise<void> {
        const list = await listAlarp()

        setAlarpList(list)
    }
    async function getUserInfo(): Promise<void> {
        try {
            const userInfo = await getAlarmSettings(currentUser)
            setUserInfo(userInfo)
        } catch (error) {
            globalNotification.error(error.message ?? i18n('An error occurred on get user settings.'))
        }
    }

    async function getAllBowties(): Promise<void> {
        try {
            let bowties: IBowtie[] = []

            const assetFilter = null
            // const resourceGroups = await getResourceGroups()
            const data = await listAllBowtiesWithDetails(assetFilter)
            //
            bowties = data.data

            const bowtieWithEvent: BowtieView[] = bowties.filter(isValidBowtie).map(getBowtieView)
            setBowtieViews(bowtieWithEvent)
            setInLoading(false)
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on list bowties.'))
        }
    }

    const handleGetBarrierStatus = async (): Promise<void> => {
        try {
            const data = await getBarrierStatus()

            const newState = data
                .sort((s1, s2) => s2.priorityOrder - s1.priorityOrder)
                .reduce((prev, s) => prev.set(s.id, s), new Map<number, IBarrierStatusSpecialStatus>())
            setStatusMap(newState)

            setBarrierStatusList(data)
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on list barrier status.'))
        }
    }

    useEffect(() => {
        return () => clearAllStore() // ComponentWillUnmount
    }, [])

    useEffect(() => {
        getBowtiesFromAlarm()
    }, [alarmData])

    useEffect(() => {
        getAlarp()
        getAllBowties()
        getUserInfo()
        handleGetBarrierStatus()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!selectedBowtie) {
            clearAllStore()
        }
    }, [selectedBowtie])

    useEffect(() => {
        if (bowtieViews?.length && state) {
            bowtieViews.forEach(btEvent => btEvent.fillWithEvent(state))
        }
    }, [state, bowtieViews]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (state?.alarms?.size > 0) {
            setAlarms(new Map(state.alarms))
        }
    }, [state])

    useEffect(() => {
        if (state.event) {
            dispatchAlarmSound(state.event)
        }
    }, [state.event])

    if (inLoading) {
        return (
            <AbsoluteLoadingContainer>
                <img src={LoadingImage} alt="loading" />
            </AbsoluteLoadingContainer>
        )
    }

    const getMethodAlarpTresholds = (methodType: string): IAlarpThreshold[] => {
        const alarpFilteredMethod = alarpList.filter(alarp => alarp.methodTypeName === methodType)
        const { alarpThresholds } = alarpFilteredMethod[0]

        return alarpThresholds.sort((a, b) => Math.max(a.alarpStatus.priorityOrder, b.alarpStatus.priorityOrder))
    }

    return bowtieViews?.length ? (
        <Provider store={store}>
            <BowtieStatusContextProvider statusMap={statusMap}>
                <OverviewContainer>
                    {selectedBowtie ? (
                        <Bowtie
                            width={width}
                            height={height}
                            bowtieData={bowtieViews.find(bv => bv.bowtie.id === selectedBowtie)?.bowtie}
                            onCloseBowtie={() => setSelectedBowtie(null)}
                            event={bowtieViews.find(bv => bv.bowtie.id === selectedBowtie)}
                            extraInfo={state.extraInfo}
                            alarpThresholds={getMethodAlarpTresholds(
                                bowtieViews.find(bv => bv.bowtie.id === selectedBowtie)?.bowtie.methodType
                            )}
                            customZoomLevels={widget?.jsonConfig?.customZoomLevels}
                        />
                    ) : showAlarmsDashboard ? (
                        <AlarmsDashboard
                            setShowAlarmsDashboard={setShowAlarmsDashboard}
                            asset={assetToAlarm}
                            alarms={alarms}
                            alarmData={alarmData}
                            setAlarmData={setAlarmData}
                            setSelectedBowtie={setSelectedBowtie}
                            alarmBowties={alarmBowties}
                        />
                    ) : (
                        <Overview
                            setSelectedBowtie={setSelectedBowtie}
                            bowtiesView={bowtieViews}
                            alarpList={alarpList}
                            width={width}
                            height={height}
                            widget={widget}
                            setShowAlarmsDashboard={setShowAlarmsDashboard}
                            alarms={alarms}
                            setAssetToAlarm={setAssetToAlarm}
                            assetToAlarm={assetToAlarm}
                            alarmData={alarmData}
                            setAlarmData={setAlarmData}
                            alarmBowties={alarmBowties}
                            userData={userInfo}
                            barrierStatusList={barrierStatusList}
                        />
                    )}
                </OverviewContainer>
            </BowtieStatusContextProvider>
        </Provider>
    ) : (
        <AbsoluteLoadingContainer>
            <h3>{i18n('There are no Bowties yet')}</h3>
            <Button
                bsStyle="primary"
                style={{ borderRadius: 3 }}
                onClick={() => (window.location.href = `/#/config/${BOWTIE_SETTINGS_URL}`)}
            >
                {i18n('Go to bowtie editor')}
            </Button>
        </AbsoluteLoadingContainer>
    )
}
